import React, { useState, useEffect } from 'react';
import { Box, Grid, Card, CardContent, Typography, useTheme, useMediaQuery, CircularProgress } from '@mui/material';
import { 
  ShoppingCart as ShoppingCartIcon,
  Category as CategoryIcon,
  Star as StarIcon,
  TrendingUp as TrendingUpIcon 
} from '@mui/icons-material';
import api from '../utils/api';

const StatCard = ({ title, value, icon: Icon, color }) => {
  const theme = useTheme();
  
  return (
    <Card 
      sx={{
        height: '100%',
        display: 'flex',
        position: 'relative',
        overflow: 'hidden',
        transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: theme.shadows[10],
        },
      }}
    >
      <CardContent sx={{ width: '100%', p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Icon sx={{ color: color, fontSize: { xs: 30, sm: 40 }, mr: 1 }} />
          <Typography variant="h6" component="div" sx={{ color: 'text.secondary', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
            {title}
          </Typography>
        </Box>
        <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', fontSize: { xs: '1.5rem', sm: '2rem' } }}>
          {value}
        </Typography>
        <Box
          sx={{
            position: 'absolute',
            right: -20,
            bottom: -20,
            opacity: 0.1,
            transform: 'rotate(-15deg)',
          }}
        >
          <Icon sx={{ fontSize: 100, color: color }} />
        </Box>
      </CardContent>
    </Card>
  );
};

const Dashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState({
    productsCount: 0,
    categoriesCount: 0,
    averageRating: 0,
    monthlySales: 0
  });

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const [productsRes, categoriesRes] = await Promise.all([
          api.get('/products'),
          api.get('/categories')
        ]);

        // Вычисляем средний рейтинг всех продуктов
        const products = productsRes.data;
        const productsWithRating = products.filter(p => p.averageRating > 0);
        const averageRating = productsWithRating.length > 0
          ? (productsWithRating.reduce((sum, p) => sum + p.averageRating, 0) / productsWithRating.length).toFixed(1)
          : 0;

        setDashboardData({
          productsCount: products.length,
          categoriesCount: categoriesRes.data.length,
          averageRating: averageRating,
          monthlySales: 0 // TODO: Implement monthly sales calculation
        });
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setError('Ошибка при загрузке данных');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const stats = [
    { 
      title: 'Всего продуктов',
      value: dashboardData.productsCount,
      icon: ShoppingCartIcon,
      color: theme.palette.primary.main 
    },
    { 
      title: 'Категории',
      value: dashboardData.categoriesCount,
      icon: CategoryIcon,
      color: theme.palette.secondary.main 
    },
    { 
      title: 'Средний рейтинг',
      value: dashboardData.averageRating,
      icon: StarIcon,
      color: theme.palette.warning.main 
    },
    { 
      title: 'Продажи за месяц',
      value: `$${dashboardData.monthlySales}`,
      icon: TrendingUpIcon,
      color: theme.palette.success.main 
    }
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Typography 
        variant="h4" 
        sx={{ 
          mb: 4,
          fontSize: { xs: '1.5rem', sm: '2rem' },
          fontWeight: 'bold',
          color: 'text.primary'
        }}
      >
        Обзор панели управления
      </Typography>
      
      <Grid container spacing={isMobile ? 2 : 3}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <StatCard {...stat} />
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 4 }}>
        <Typography 
          variant="h5" 
          sx={{ 
            mb: 2,
            fontSize: { xs: '1.2rem', sm: '1.5rem' },
            fontWeight: 'bold',
            color: 'text.primary'
          }}
        >
          Последняя активность
        </Typography>
        <Card>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="body1" color="text.secondary">
              Нет последней активности для отображения.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default Dashboard;
